import { useState } from 'react';

interface ContactFormProps {
  isOpen: boolean;
  onClose: () => void;
  setIsAlertOpen: (isOpen: boolean) => void;
}

export default function ContactForm({ isOpen, onClose, setIsAlertOpen }: ContactFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // バリデーション
    const name = formData.get('name') as string;
    const company = formData.get('company') as string;
    const email = formData.get('email') as string;
    const payment = formData.get('payment') as string;
    const content = formData.get('content') as string;

    if (!name || !company || !email || !payment || !content) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('./', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setIsAlertOpen(true);
        setSubmitStatus('success');
        form.reset();
        setTimeout(() => {
          onClose();
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-2xl font-bold text-gray-900">キーワード検索依頼フォーム</h2>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8" data-readdy-form>
          <div className="space-y-6 text-left">
            {/* お名前 */}
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                お名前<span className="text-[#eb4b3f] ml-1">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#eb4b3f] focus:border-transparent outline-none transition-all text-sm"
                placeholder="山田 太郎"
              />
            </div>

            {/* 会社名・組織名 */}
            <div>
              <label htmlFor="company" className="block text-sm font-semibold text-gray-900 mb-2">
                会社名・組織名<span className="text-[#eb4b3f] ml-1">*</span>
              </label>
              <input
                type="text"
                id="company"
                name="company"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#eb4b3f] focus:border-transparent outline-none transition-all text-sm"
                placeholder="株式会社〇〇"
              />
            </div>

            {/* メールアドレス */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                メールアドレス<span className="text-[#eb4b3f] ml-1">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#eb4b3f] focus:border-transparent outline-none transition-all text-sm"
                placeholder="example@company.co.jp"
              />
            </div>

            {/* 電話番号 */}
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                電話番号
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#eb4b3f] focus:border-transparent outline-none transition-all text-sm"
                placeholder="03-1234-5678"
              />
            </div>

            {/* 決済方法 */}
            <div>
              <label htmlFor="payment" className="block text-sm font-semibold text-gray-900 mb-2">
                決済方法<span className="text-[#eb4b3f] ml-1">*</span>
              </label>
              <select
                id="payment"
                name="payment"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#eb4b3f] focus:border-transparent outline-none transition-all text-sm cursor-pointer"
              >
                <option value="">選択してください</option>
                <option value="請求書払い">請求書払い</option>
                <option value="クレジット払い">クレジット払い</option>
              </select>
            </div>

            {/* 記入例 */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
              <h4 className="text-sm font-bold text-gray-900 mb-3">【記入例】</h4>
              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <p className="font-semibold text-gray-900 mb-1">・調査背景</p>
                  <p className="leading-relaxed">学会で「○○病に対して△△剤が欧米人に比べて日本人によく効く」という内容の発表があり、それに関連する文献を探しています。</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-1">・主題</p>
                  <p className="leading-relaxed">○○病×△△剤×日本人×欧米人</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-1">・希望言語</p>
                  <p className="leading-relaxed">英語・日本語両方</p>
                </div>
              </div>
            </div>

            {/* ご依頼内容 */}
            <div>
              <label htmlFor="content" className="block text-sm font-semibold text-gray-900 mb-2">
                ご依頼内容<span className="text-[#eb4b3f] ml-1">*</span>
              </label>
              <textarea
                id="content"
                name="content"
                required
                maxLength={500}
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#eb4b3f] focus:border-transparent outline-none transition-all resize-none text-sm"
                placeholder="ご依頼内容を詳しくお書きください(500文字以内)"
              ></textarea>
              <p className="text-xs text-gray-500 mt-1">※500文字以内でご入力ください</p>
            </div>

            {/* 送信ステータス */}
            {submitStatus === 'success' && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
                <i className="ri-check-line mr-2"></i>
                送信が完了しました。ありがとうございます。
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                <i className="ri-error-warning-line mr-2"></i>
                送信に失敗しました。必須項目をご確認ください。
              </div>
            )}

            {/* 送信ボタン */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 bg-gradient-to-r from-[#eb4b3f] to-[#f0945b] text-white font-semibold rounded-lg hover:from-[#d43d31] hover:to-[#e08549] transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  送信中...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-fill mr-2"></i>
                  送信する
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
